// The desired-state controller for a Meet space's recording config.
//
// Loom owns only what it wrote: it re-patches a space when the live config is
// its own earlier write, and reports (never overwrites) a value a human
// changed. Ending, disconnect and erasure restore the config the space had
// before Loom. A "policy default" (`AUTO_GENERATION_TYPE_UNSPECIFIED`) cannot be
// written back, so restoring it writes an explicit OFF.
//
// Pure, browser-safe.

import { z } from "zod";

import type { RecordingStart, SpaceStatus } from "./vocabulary.js";

/** Google's `AutoGenerationType`. UNSPECIFIED means "the user's policy default". */
export const AUTO_GENERATION_TYPES = ["ON", "OFF", "AUTO_GENERATION_TYPE_UNSPECIFIED"] as const;
export type AutoGenerationType = (typeof AUTO_GENERATION_TYPES)[number];

/**
 * The part of Google's `SpaceConfig.artifactConfig` Loom reads and writes, in
 * Google's own shape (it is the `spaces.patch` body under `config.artifactConfig`
 * and is stored as is in `prior_artifact_config` / `last_written_config`).
 */
// Google's JSON (proto3) omits a field that holds its enum's zero value, and
// UNSPECIFIED is that zero value: a missing field or sub-object reads as
// UNSPECIFIED (the policy default), never as a parse failure. Unverified
// against a real capture until owner task O1.
const UNSPECIFIED = "AUTO_GENERATION_TYPE_UNSPECIFIED" as const;
const generation = z.enum(AUTO_GENERATION_TYPES).default(UNSPECIFIED);

export const ArtifactConfigSchema = z.object({
  recordingConfig: z
    .object({ autoRecordingGeneration: generation })
    .default({ autoRecordingGeneration: UNSPECIFIED }),
  transcriptionConfig: z
    .object({ autoTranscriptionGeneration: generation })
    .default({ autoTranscriptionGeneration: UNSPECIFIED }),
});
export type ArtifactConfig = z.infer<typeof ArtifactConfigSchema>;

export const artifactConfig = (
  recording: AutoGenerationType,
  transcription: AutoGenerationType,
): ArtifactConfig => ({
  recordingConfig: { autoRecordingGeneration: recording },
  transcriptionConfig: { autoTranscriptionGeneration: transcription },
});

/** Recording and transcription both on: what an `auto` space runs with. */
export const ARTIFACTS_ON: ArtifactConfig = artifactConfig("ON", "ON");

/** Both off: a `manual` space, where the host starts recording in Meet by hand. */
export const ARTIFACTS_OFF: ArtifactConfig = artifactConfig("OFF", "OFF");

/** The auto-artifact config a live space runs with, by its recording start. */
export const artifactConfigFor = (recordingStart: RecordingStart): ArtifactConfig =>
  recordingStart === "manual" ? ARTIFACTS_OFF : ARTIFACTS_ON;

export const sameArtifactConfig = (a: ArtifactConfig, b: ArtifactConfig): boolean =>
  a.recordingConfig.autoRecordingGeneration === b.recordingConfig.autoRecordingGeneration &&
  a.transcriptionConfig.autoTranscriptionGeneration ===
    b.transcriptionConfig.autoTranscriptionGeneration;

const writable = (v: AutoGenerationType): AutoGenerationType =>
  v === "AUTO_GENERATION_TYPE_UNSPECIFIED" ? "OFF" : v;

/**
 * The config to write back when Loom lets go of a space: the prior config,
 * with UNSPECIFIED as OFF (Google documents UNSPECIFIED as never returned, so
 * it cannot be written). Null when no prior was stored (a space Loom created):
 * nothing to restore.
 */
export const restoreTarget = (prior: ArtifactConfig | null): ArtifactConfig | null =>
  prior === null
    ? null
    : artifactConfig(
        writable(prior.recordingConfig.autoRecordingGeneration),
        writable(prior.transcriptionConfig.autoTranscriptionGeneration),
      );

export interface SpaceConfigState {
  readonly status: SpaceStatus;
  readonly priorArtifactConfig: ArtifactConfig | null;
  /** `manual`: the host starts recording by hand, so Loom wants auto OFF. */
  readonly recordingStart: RecordingStart;
}

/**
 * What the space's config should be now. Active or awaiting takeover → ON for
 * an `auto` space, OFF for a `manual` one: Loom never turns auto-recording on
 * in a space whose owner chose to start it by hand. Ended → the restore
 * target of its prior config. Host lost → null: Loom can no longer act on the
 * space, so it wants nothing.
 */
export const desiredArtifactConfig = (space: SpaceConfigState): ArtifactConfig | null => {
  switch (space.status) {
    case "active":
    case "pending_takeover":
      return artifactConfigFor(space.recordingStart);
    case "ended":
      return restoreTarget(space.priorArtifactConfig);
    case "host_lost":
      return null;
  }
};

/**
 * - `none`: nothing to do (live already desired, or nothing desired);
 * - `patch`: live is Loom's own last write, now out of date → patch to desired;
 * - `human_override`: someone else changed it → do not patch, ask the owner.
 */
export type ConfigAction = "none" | "patch" | "human_override";

export const configAction = (input: {
  readonly live: ArtifactConfig;
  readonly desired: ArtifactConfig | null;
  readonly lastWritten: ArtifactConfig | null;
}): ConfigAction => {
  if (input.desired === null) return "none";
  if (sameArtifactConfig(input.live, input.desired)) return "none";
  if (input.lastWritten !== null && sameArtifactConfig(input.live, input.lastWritten)) {
    return "patch";
  }
  return "human_override";
};
